/* eslint-disable @shopify/jsx-no-hardcoded-content */
"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChartBig, Diff } from "lucide-react";
import * as React from "react";
import BudgetViewTable from "./BudgetViewTable";
import BudgetCompareDialog from "./BudgetCompareDialog";
import Autocomplete from "@/components/ui/autocomplete";
import BudgetDetailsDialog from "./BudgetDetailsDialog";
import { api } from "@/lib/trpc/client";
import type { BudgetEntry, PublicBudgetData } from "./types";
import { useTranslations, useLocale } from "use-intl";
import { translations } from "@/messages";
interface BudgetViewProps {
  institutionId: string;
}
const BudgetView: React.FC<BudgetViewProps> = ({
  institutionId
}) => {
  const locale = useLocale() as keyof typeof translations;
  const t = useTranslations("budget");
  const [selectedYear, setSelectedYear] = React.useState<string | undefined>(undefined);
  const [selectedBudget, setSelectedBudget] = React.useState<PublicBudgetData | undefined>(undefined);
  const availableYears = api.budgetPublicRouter.getAvailableBudgetYears.useQuery({
    institutionId: institutionId
  });
  const budgetsQuery = api.budgetPublicRouter.getBudgetsForInstitution.useQuery({
    institutionId: institutionId,
    year: selectedYear
  }, {
    enabled: !!institutionId
  });
  const years = availableYears.data?.map(available => available.year) || [];
  const calculateTotals = (entries: BudgetEntry[]) => {
    let totalRevenue = 0;
    let totalExpense = 0;
    entries.forEach(entry => {
      totalRevenue += entry.firstTrimester + entry.secondTrimester + entry.thirdTrimester + entry.fourthTrimester;
      totalExpense += entry.amount;
    });
    return {
      totalRevenue,
      totalExpense
    };
  };
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "RON"
    }).format(value);
  };
  const [comparePair, setComparePair] = React.useState<[PublicBudgetData, PublicBudgetData] | [null, null]>([null, null]);
  const compareBudgets = (budget1: PublicBudgetData, budget2: PublicBudgetData) => {
    setComparePair([budget1, budget2]);
  };
  return <Card className="h-auto overflow-hidden overflow-y-auto rounded-lg border-none shadow-lg" data-sentry-element="Card" data-sentry-component="BudgetView" data-sentry-source-file="BudgetView.tsx">
      <CardHeader className="flex flex-row items-center justify-between gap-2 rounded-t-lg p-6" data-sentry-element="CardHeader" data-sentry-source-file="BudgetView.tsx">
        <div>
          <CardTitle className="font-semibold text-glow" data-sentry-element="CardTitle" data-sentry-source-file="BudgetView.tsx">{t("title")}</CardTitle>
          <CardDescription className="mt-1" data-sentry-element="CardDescription" data-sentry-source-file="BudgetView.tsx">{t("description")}</CardDescription>
        </div>
        <div>
          <Autocomplete placeholder={selectedYear || translations[locale]["institution"]["budgets"]["year"]} options={years.map(year => ({
          label: year.toString(),
          value: year.toString()
        }))} initialValue={selectedYear} onChangeAction={value => setSelectedYear(value.value.toString())} width={120} data-sentry-element="Autocomplete" data-sentry-source-file="BudgetView.tsx" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-6 pb-0" data-sentry-element="CardContent" data-sentry-source-file="BudgetView.tsx">
        <ol className="relative mt-2 border-s border-glow/20">
          {budgetsQuery.data?.map((budgetData, index) => {
          const {
            totalRevenue,
            totalExpense
          } = calculateTotals(budgetData.budgetEntries);
          return <li key={index} className="mb-8">
                <div className="absolute -start-3.5 h-7 w-7 rounded-full border-4 border-background bg-glow"></div>
                <div className="pl-6">
                  <div className="flex w-full flex-col justify-between tablet:flex-row">
                    <div className="flex pr-8">
                      <div>
                        <h2 className="flex items-center text-lg font-semibold text-glow">
                          {t("budgetFor", {
                        year: budgetData.year
                      })}{" "}
                          {budgetData.createdAt?.toLocaleDateString(locale)}
                          <BarChartBig size={20} className="ml-2 cursor-pointer text-primary" onClick={() => setSelectedBudget(budgetData)} />
                        </h2>
                        <h3 className="text-sm text-primary/80">
                          {t("rectificationFor", {
                        year: budgetData.year,
                        announcedBy: budgetData.announcedBy
                      })}
                        </h3>
                      </div>
                    </div>
                    <div className="my-4 rounded-xl border border-glow/20 bg-glow/5 px-4 py-3 text-sm leading-relaxed tablet:-mt-4 tablet:w-fit">
                      <p className="text-green-500">
                        {t("revenue")}: {formatCurrency(totalRevenue)}
                      </p>
                      <p className="text-red-500">
                        {t("expense")}: {formatCurrency(totalExpense)}
                      </p>
                    </div>
                  </div>
                  <BudgetViewTable budgetData={budgetData} />
                </div>

                {budgetsQuery.data[index] && budgetsQuery.data[index + 1] && <div className="mt-8 flex w-full items-center justify-center" onClick={() => {
              const currentBudget = budgetsQuery.data[index];
              const previousBudget = budgetsQuery.data[index + 1];
              if (!currentBudget || !previousBudget) {
                return;
              }
              compareBudgets(currentBudget, previousBudget);
            }}>
                    <div className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-glow text-background hover:bg-glow/80">
                      <Diff size={20} />
                    </div>
                  </div>}
              </li>;
        })}
        </ol>
      </CardContent>
      <BudgetCompareDialog budgetPair={comparePair} onOpenChange={open => {
      if (!open) {
        setComparePair([null, null]);
      }
    }} data-sentry-element="BudgetCompareDialog" data-sentry-source-file="BudgetView.tsx" />
      <BudgetDetailsDialog budget={selectedBudget || null} onOpenChange={open => {
      if (!open) {
        setSelectedBudget(undefined);
      }
    }} data-sentry-element="BudgetDetailsDialog" data-sentry-source-file="BudgetView.tsx" />
    </Card>;
};
export default BudgetView;